import type { StringSchemaOptions, ValidatorType } from "shelving";
import { DATA, Feedback, StringSchema, getOptionalRef, getRef, isArrayItem } from "shelving";
import { CONTENT_ENTITY, CREATOR_ENTITY, type CreatorEntity } from "./entity.js";

/** Banned usernames that might clash with top-level routes. */
const BANNED_USERNAMES = [
	"challenge", //
	"challenges",
	"reward",
	"rewards",
	"badge",
	"badges",
	"background",
	"backgrounds",
	"reaction",
	"reactions",
	"frame",
	"frames",
	"bling",
	"blings",
	"earned",
	"note",
	"notes",
	"user",
	"users",
	"profile",
	"profiles",
	"group",
	"groups",
	"post",
	"posts",
	"me",
	"settings",
	"about",
	"member",
	"members",
	"menu",
	"notifications",
	"login",
	"logout",
];

/** Schema for valid username field. */
export class UsernameSchema extends StringSchema {
	constructor(options: Omit<StringSchemaOptions, "min" | "max" | "match" | "sanitizer">) {
		super({
			title: "Username",
			placeholder: "e.g. best-username",
			...options,
			min: 2,
			max: 32,
		});
	}
	override validate(unsafeValue: unknown): string {
		const str = super.validate(unsafeValue);
		const ref = str && getOptionalRef(str);
		if (!ref) throw new Feedback("Must include A-Z");
		if (BANNED_USERNAMES.includes(ref)) throw new Feedback("Invalid username");
		return str;
	}
	override sanitize(uncleanString: string): string {
		return uncleanString.replace(USERNAME_SPACES, "_").replace(USERNAME_STRIP, "");
	}
}
const USERNAME_SPACES = /[_-\s]+/g;
const USERNAME_STRIP = /[^a-zA-Z0-9_]+|^[^a-zA-Z0-9]+|[^a-zA-Z0-9]+$/g;

/** Usernames for profiles and groups are stored in the database in the `username` table. */
export const USERNAME_SCHEMA = DATA({
	creator: CREATOR_ENTITY,
});
export type UsernameData = ValidatorType<typeof USERNAME_SCHEMA>;

/** Get the ID of a shortcut item that corresponds to a given creator and ref. */
export const getUsernameID: (username: string) => string = getRef;

/** List of entities that have usernames. */
export const USERNAMED_ENTITY_TYPES = ["profile", "group"] as const;
export type UsernamedEntityType = (typeof USERNAMED_ENTITY_TYPES)[number];
export const isUsernamedEntityType = (type: string): type is UsernamedEntityType => isArrayItem(USERNAMED_ENTITY_TYPES, type);

/**
 * shortcuts to content owned by groups and users.
 * - Powers `@dhoulb/5-day-blast` friendly tags rather than `challenge:12345`
 * - `ref` is based on the name of the content, e.g. `5 Day Blast` → `5dayblast` and `Order Online!` → `orderonline`
 * - Each piece of content must have a unique name.
 * - Effectively users own the namespace under their own username, e.g. `@dhoulb/etc`
 */
export const SHORTCUT_SCHEMA = DATA({
	creator: CREATOR_ENTITY,
	target: CONTENT_ENTITY,
});
export type ShortcutData = ValidatorType<typeof SHORTCUT_SCHEMA>;

/** Get the ID of a shortcut item that corresponds to a given creator and ref. */
export const getShortcutID = (creator: CreatorEntity, slug: string) => `${creator}:${getRef(slug)}`;
